import React, { useEffect, useRef, useState, useCallback } from 'react';
import './styles.css';
import { supabase } from '../lib/supabase';
import { mockTextures } from '../lib/mockupData';
import { preloadTextureImages } from '../utils/textureImageKitService';
import TextPressure from '../components/TextAnimations/TextPressure';
import LogoKosma from '../assets/Image/logokosma.svg';
import {
  DEFAULT_SQUARE_SIZE,
  DEFAULT_BASE_OFFSET,
  DEFAULT_ANGLE,
  TextureCard,
  Tooltip,
  DetailCard,
  FilterPanel,
  calculateCardPositions,
  getCardHoverClass,
  updateContainerTransform
} from '../utils/cardStackUtils';
import { useTheme } from '@geist-ui/core';

// Configuration - set to true to use local mock data instead of Supabase
const USE_LOCAL_DATA = false;

// Costante per l'animazione iniziale
const INITIAL_SPACING = 2000; // Valore alto per lo spacing iniziale

function Page1() {
  // State variables
  const [texturesData, setTexturesData] = useState([]);
  const [activeMaterialFilters, setActiveMaterialFilters] = useState({});
  const [activeCategoryFilters, setActiveCategoryFilters] = useState({});
  const isAnimating = false;
  const [simulatedScrollY, setSimulatedScrollY] = useState(INITIAL_SPACING); // Iniziamo con uno spacing alto
  const [isInitialAnimationActive, setIsInitialAnimationActive] = useState(true); // Nuovo stato per l'animazione iniziale
  const [shouldShowUI, setShouldShowUI] = useState(false); // Nuovo stato per controllare la visibilità degli elementi UI
  const [zoomFactor, setZoomFactor] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  
  // Tooltip state
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    position: { x: 0, y: 0 },
    content: '',
    hoveredIndex: null
  });
  
  // Card hover states - only track what's necessary
  const [hoveredCard, setHoveredCard] = useState({
    index: null,
    adjacentLeft: null,
    adjacentRight: null
  });

  // Selected texture state
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // References
  const sceneRef = useRef(null);
  const stackContainerRef = useRef(null);
  const animationFrameRef = useRef(null); // Nuovo ref per l'animazione
  
  // Category and material sets for filter UI
  const [materialSet, setMaterialSet] = useState({});
  const [categorySet, setCategorySet] = useState({});

  // Add state for cursor-centered zooming
  const [focusedCardIndex, setFocusedCardIndex] = useState(null);

  // Add search term state
  const [searchTerm, setSearchTerm] = useState('');

  // Add state to track scrolling
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);

  // Tooltip handlers
  const showTooltip = useCallback((index, texture) => {
    if (isAnimating) return;
    
    // Update hover states for proper CSS classes
    setHoveredCard({
      index,
      adjacentLeft: index > 0 ? index - 1 : null,
      adjacentRight: index < texturesData.length - 1 ? index + 1 : null
    });
    
    // Only update tooltip content without position to reduce updates
    setTooltipState(prev => ({
      ...prev,
      visible: true,
      content: `<strong>Material:</strong> ${texture.material_name}<br><strong>Texture:</strong> ${texture.texture_name}`,
      hoveredIndex: index
    }));
  }, [isAnimating, texturesData.length]);

  const updateTooltipPosition = useCallback((x, y) => {
    // Only update position without re-rendering the entire tooltip
    setTooltipState(prev => ({ ...prev, position: { x, y } }));
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltipState(prev => ({ ...prev, visible: false, hoveredIndex: null }));
    setHoveredCard({ index: null, adjacentLeft: null, adjacentRight: null });
  }, []);

  // Effect per mostrare gli elementi UI con fade-in dopo l'animazione
  useEffect(() => {
    if (!isInitialAnimationActive) {
      // Piccolo timeout per assicurarsi che l'animazione delle card sia completata
      const timer = setTimeout(() => {
        setShouldShowUI(true);
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isInitialAnimationActive]);

  // Animazione iniziale per le card
  useEffect(() => {
    if (isInitialAnimationActive && texturesData.length > 0) {
      const startTime = performance.now();
      const duration = 500; // Durata dell'animazione in millisecondi
      const startSpacing = INITIAL_SPACING;
      const endSpacing = 0; // Spacing finale
      
      const animateSpacing = (timestamp) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Funzione easing per un movimento più naturale e fluido (easeOutQuint)
        const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
        const easedProgress = easeOutQuint(progress);
        
        const currentSpacing = startSpacing - (startSpacing - endSpacing) * easedProgress;
        setSimulatedScrollY(currentSpacing);
        
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animateSpacing);
        } else {
          setIsInitialAnimationActive(false);
        }
      };
      
      animationFrameRef.current = requestAnimationFrame(animateSpacing);
      
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [isInitialAnimationActive, texturesData.length]);

  // Fix the wheel handler to work after deselection and allow scrolling when card is selected
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    
    // Non processare eventi wheel durante l'animazione iniziale
    if (isInitialAnimationActive) {
      return;
    }
    
    // Don't process wheel events during animation
    if (isAnimating) {
      return;
    }
    
    // Set scrolling state to true
    setIsScrolling(true);
    
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Set a timeout to mark scrolling as finished after a short delay
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
    
    if (e.ctrlKey) {
      const angleRad = DEFAULT_ANGLE * (Math.PI / 180);
      
      if (!stackContainerRef.current) return;
      const rect = stackContainerRef.current.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const pX = offsetX - centerX;
      const pY = offsetY - centerY;
      
      const dot = pX * Math.cos(angleRad) + pY * (-Math.sin(angleRad));
      const projX = dot * Math.cos(angleRad);
      const projY = dot * (-Math.sin(angleRad));
      
      const newCenterX = centerX + projX;
      const newCenterY = centerY + projY;
      const originX = (newCenterX / rect.width) * 100;
      const originY = (newCenterY / rect.height) * 100;
      stackContainerRef.current.style.transformOrigin = `${originX}% ${originY}%`;
      
      const zoomChange = e.deltaY < 0 ? 1.05 : 0.95;
      setZoomFactor(prev => {
        const newZoom = prev * zoomChange;
        return Math.max(0.5, Math.min(newZoom, 3));
      });
    } else {
      // For regular scrolling (changing card spacing)
      // Use requestAnimationFrame for smoother scrolling performance
      requestAnimationFrame(() => {
        // Maintain the current hovered state if there is one
        const hoveredState = hoveredCard.index;
        
      if (stackContainerRef.current) {
        const rect = stackContainerRef.current.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        
          // Find the card nearest to cursor if nothing is hovered yet
          if (hoveredState === null) {
        let closestCardIndex = null;
        let minDistance = Infinity;
        
        // Loop through visible cards to find the closest one
        Array.from(stackContainerRef.current.children).forEach((card, index) => {
          const cardX = parseFloat(card.dataset.originalX) || 0;
          const cardY = parseFloat(card.dataset.originalY) || 0;
          
          // Calculate cursor distance to the card
          const dx = cardX - cursorX;
          const dy = cardY - cursorY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < minDistance) {
            minDistance = distance;
            closestCardIndex = index;
          }
        });
        
        if (closestCardIndex !== null) {
          setFocusedCardIndex(closestCardIndex);
            }
          } else {
            // If we already have a hover state, keep that card's index as the focus
            setFocusedCardIndex(hoveredState);
        }
      }
        
        // Use a smaller delta for smoother scrolling when a card is selected
        const scrollMultiplier = selectedIndex !== null ? 0.5 : 1.0;
      
      setSimulatedScrollY(prev => {
        const MAX_VIRTUAL_SCROLL = 2000;
          return Math.max(0, Math.min(MAX_VIRTUAL_SCROLL, prev + (e.deltaY * scrollMultiplier)));
        });
      });
    }
  }, [isAnimating, hoveredCard, selectedIndex, isInitialAnimationActive]);

  // Transform update functions
  const updatePanOffset = useCallback((e) => {
    // Non aggiornare il pan durante l'animazione iniziale
    if (isInitialAnimationActive) return;
    
    // Don't update pan offset if we're in an interactive state
    if (isAnimating || tooltipState.hoveredIndex !== null) return;
    
    if (!sceneRef.current) return;
    const sceneRect = sceneRef.current.getBoundingClientRect();
    const extraSpacing = simulatedScrollY / 20;
    
    if (extraSpacing <= 0) {
      setPanOffset({ x: 0, y: 0 });
      return;
    }
    
    const mouseX = e.clientX - sceneRect.left;
    const relativeX = ((mouseX / sceneRect.width) - 0.5) * 2;
    
    const panActivationThreshold = 0.6;
    if (Math.abs(relativeX) < panActivationThreshold) {
      setPanOffset({ x: 0, y: 0 });
      return;
    }
    
    let panNormalized = (Math.abs(relativeX) - panActivationThreshold) / (1 - panActivationThreshold);
    panNormalized = relativeX < 0 ? -panNormalized : panNormalized;
    
    const angleRad = DEFAULT_ANGLE * (Math.PI / 180);
    const squareSize = DEFAULT_SQUARE_SIZE;
    const baseOffset = DEFAULT_BASE_OFFSET;
    const depth = texturesData.length;
    const effectiveOffset = baseOffset + simulatedScrollY / 20;
    const stackLength = (depth - 1) * effectiveOffset + squareSize;
    
    const containerWidth = sceneRect.width;
    const containerHeight = sceneRect.height;
    const sceneProjectionLength = containerWidth * Math.abs(Math.cos(angleRad)) +
                               containerHeight * Math.abs(Math.sin(angleRad));
    
    let maxPan = 0;
    if (stackLength > sceneProjectionLength) {
      maxPan = 30;
    }
    
    const panScalar = -panNormalized * maxPan;
    setPanOffset({
      x: panScalar * Math.cos(angleRad),
      y: panScalar * (-Math.sin(angleRad))
    });
  }, [isAnimating, simulatedScrollY, texturesData.length, tooltipState.hoveredIndex, isInitialAnimationActive]);

  // Updated filter change handler
  const handleFilterChange = useCallback((type, value) => {
    if (type === 'material') {
      setActiveMaterialFilters(prev => ({
        ...prev,
        [value]: !prev[value]
      }));
    } else if (type === 'category') {
      setActiveCategoryFilters(prev => ({
        ...prev,
        [value]: !prev[value]
      }));
    }
  }, []);

  // Add search term handler
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  // Fetch textures data from Supabase or use mock data
  useEffect(() => {
    const fetchTexturesData = async () => {
      try {
        let data;
        if (USE_LOCAL_DATA) {
          data = mockTextures;
        } else {
          const { data: supabaseData, error } = await supabase
            .from('texture_duplicate')
            .select('*');
          
          if (error) throw error;
          data = supabaseData;
        }
        
        if (data && data.length > 0) {
          setTexturesData(data);
          
          // Extract texture IDs for preloading
          const textureIds = data.map(texture => texture.id).filter(Boolean);
          
          // Preload all textures at once
          preloadTextureImages(textureIds, '0', { width: 512 })
            .then(results => {
              console.log(`Preloaded ${Object.keys(results.loaded).length} textures successfully`);
              if (Object.keys(results.failed).length > 0) {
                console.warn(`Failed to load ${Object.keys(results.failed).length} textures`);
              }
            })
            .catch(err => {
              console.error('Error preloading textures:', err);
            });
          
          // Build the material and category sets for filters
          const materials = {};
          const categories = {};
          
          data.forEach(texture => {
            if (texture.material_name) materials[texture.material_name] = true;
            if (texture.category_name) categories[texture.category_name] = true;
          });
          
          setMaterialSet(materials);
          setCategorySet(categories);
          
          // Initialize filters with all values set to false (inactive)
          if (Object.keys(activeMaterialFilters).length === 0) {
            const inactiveMaterialFilters = {};
            Object.keys(materials).forEach(material => {
              inactiveMaterialFilters[material] = false;
            });
            setActiveMaterialFilters(inactiveMaterialFilters);
          }
          
          if (Object.keys(activeCategoryFilters).length === 0) {
            const inactiveCategoryFilters = {};
            Object.keys(categories).forEach(category => {
              inactiveCategoryFilters[category] = false;
            });
            setActiveCategoryFilters(inactiveCategoryFilters);
          }
        }
      } catch (error) {
        console.error('Error fetching textures data:', error);
      }
    };
    
    fetchTexturesData();
  }, [activeMaterialFilters, activeCategoryFilters]);

  // Initialize the stack on component mount
  useEffect(() => {
    // We no longer need to call loadTextures() here as it's handled in the data fetching effect
    
    // Event listeners
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchmove', e => e.preventDefault(), { passive: false });
    
    // Clean up
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', e => e.preventDefault());
      
      // Pulire l'animazione frame se esiste
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleWheel]);

  // Update container transform when pan/zoom changes
  useEffect(() => {
    updateContainerTransform(stackContainerRef, panOffset, zoomFactor);
  }, [panOffset, zoomFactor]);

  // Set up mouse move event for scene
  useEffect(() => {
    if (sceneRef.current) {
      const currentSceneRef = sceneRef.current;
      
      // Add minimal debounce for mousemove
      let debounceTimer;
      const debouncedUpdatePan = (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => updatePanOffset(e), 10);
      };
      
      currentSceneRef.addEventListener('mousemove', debouncedUpdatePan);
      currentSceneRef.addEventListener('mouseleave', () => {
        setPanOffset({ x: 0, y: 0 });
      });
      
      return () => {
        currentSceneRef.removeEventListener('mousemove', debouncedUpdatePan);
        currentSceneRef.removeEventListener('mouseleave', () => {});
        clearTimeout(debounceTimer);
      };
    }
  }, [updatePanOffset]);

  // Calculate card positions using the utility function
  const cardPositions = calculateCardPositions({
    texturesData,
    activeMaterialFilters,
    activeCategoryFilters,
    searchTerm,
    simulatedScrollY,
    focusedCardIndex,
    hoveredCard,
    selectedTexture,
    selectedIndex,
    stackContainerRef
  });

  // Add useEffect to update the selected index when filters change
  useEffect(() => {
    if (selectedTexture) {
      // Find the new index of the selected texture in the filtered list
      const newCardPositions = calculateCardPositions({
        texturesData,
        activeMaterialFilters,
        activeCategoryFilters,
        searchTerm,
        simulatedScrollY,
        focusedCardIndex,
        hoveredCard,
        selectedTexture,
        selectedIndex,
        stackContainerRef
      });
      
      const newSelectedIndex = newCardPositions.findIndex(item => 
        item.texture.id === selectedTexture.id || 
        (item.texture.texture_name === selectedTexture.texture_name && 
         item.texture.material_name === selectedTexture.material_name)
      );
      
      if (newSelectedIndex !== -1 && newSelectedIndex !== selectedIndex) {
        setSelectedIndex(newSelectedIndex);
      }
    }
  }, [activeMaterialFilters, activeCategoryFilters, searchTerm, selectedTexture, selectedIndex, texturesData, simulatedScrollY, focusedCardIndex, hoveredCard]);

  // Add texture selection handler
  const handleTextureSelect = useCallback((texture, index) => {
    if (selectedIndex === index) {
      // Deselect if already selected
      setSelectedTexture(null);
      setSelectedIndex(null);
    } else {
      // Select new texture
      setSelectedTexture(texture);
      setSelectedIndex(index);
      
      // Don't modify the z-index, let it maintain its natural stacking order
    }
  }, [selectedIndex]);

  // Add handler to close the detail panel
  const handleCloseDetailPanel = useCallback(() => {
    setSelectedTexture(null);
    setSelectedIndex(null);
  }, []);

  // Add click handler to main scene to deselect texture when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e) => {
      // Only deselect if clicking directly on the scene (not on a card or detail panel)
      if (e.target.classList.contains('scene')) {
        setSelectedTexture(null);
        setSelectedIndex(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  // Clean up scrolling timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const theme = useTheme();

  // Stili per il fade-in degli elementi UI
  const uiElementStyle = {
    opacity: shouldShowUI ? 1 : 0,
    transition: 'opacity 0.8s ease-in-out',
    visibility: shouldShowUI ? 'visible' : 'hidden'
  };

  return (
    <div className="page1" ref={sceneRef} onMouseMove={updatePanOffset} onWheel={handleWheel}>
      {/* Large KOSMA text in background */}
      <div className="kosma-background-text" style={uiElementStyle}>
        <img src={LogoKosma} alt="KOSMA Logo" />
      </div>
      
      {/* Aggiungo il componente TextPressure per il messaggio di zoom */}
      <div className="zoom-message-container" style={uiElementStyle}>
        <TextPressure
          text="Usa   lo   zoom   per   navigare   e   esplorare   le   texture!"
          flex={true}
          alpha={false}
          stroke={false}
          width={true}
          weight={true}
          italic={true}
          fontFamily="GeneralSans-Variable"
          useLocalFont={true}
          textColor={theme.type === 'customDark' ? 'rgba(220, 220, 220, 0.9)' : 'rgba(40, 40, 40, 0.9)'}
          minFontSize={16}
        />
      </div>
      
      {/* Main scene */}
      <div 
        className="scene" 
        ref={sceneRef} 
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
      >
        <div 
          className="stack-container"
          ref={stackContainerRef} 
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%'
          }}
        >
          {/* Render cards using the memoized component */}
          {cardPositions.map(({ texture, index, position, zIndex }) => (
            <TextureCard
              key={`texture-${index}-${texture.texture_name}`}
              texture={texture}
              index={index}
              position={position}
              zIndex={zIndex}
              isAnimating={isAnimating || isInitialAnimationActive}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
              updateTooltipPosition={updateTooltipPosition}
              hoverClass={getCardHoverClass(index, hoveredCard)}
              isSelected={selectedIndex === index}
              onSelect={handleTextureSelect}
              isScrolling={isScrolling}
            />
          ))}
        </div>
      </div>
      
      {/* Tooltip */}
      <div style={tooltipState.visible ? {} : uiElementStyle}>
        <Tooltip
          visible={tooltipState.visible && shouldShowUI}
          position={tooltipState.position}
          content={tooltipState.content}
        />
      </div>
      
      {/* Detail Card for selected texture */}
      {selectedTexture && (
        <div style={uiElementStyle}>
          <DetailCard
            texture={selectedTexture}
            visible={!!selectedTexture}
            onClose={handleCloseDetailPanel}
          />
        </div>
      )}
      
      {/* Update FilterPanel with search functionality */}
      <div style={uiElementStyle}>
        <FilterPanel
          materials={materialSet}
          categories={categorySet}
          activeMaterialFilters={activeMaterialFilters}
          activeCategoryFilters={activeCategoryFilters}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />
      </div>
    </div>
  );
}

export default Page1; 